import {Attachment, Exercise, ExerciseTableData, Method, PlanExercise} from "../../../../../types/domain";
import React, {useEffect, useMemo, useState} from "react";
import {generateExerciseTableData} from "../helperFunctions";
import {
    MantineReactTable,
    MRT_ColumnDef,
    MRT_ToggleFiltersButton,
    MRT_ToggleGlobalFilterButton,
    useMantineReactTable
} from "mantine-react-table";
import {ActionIcon, Checkbox, Group, Paper} from "@mantine/core";
import {IconSquareRoundedPlus} from "@tabler/icons-react";
import {MRT_Localization_DE} from "mantine-react-table/locales/de";

import "../../../../../css/components/ExercisesForm.css"
import "../../../../../css/components/Modals.css"
import {ChooseAttachmentsModal} from "../modal/ChooseAttachmentsModal";
import Cookies from "js-cookie";

type ExercisesData = {
    newExercises: Array<PlanExercise>,
    exercises: Array<Exercise>,
    allMethods: Array<Method>,
    showErrorModal: (error: string) => void
    showOwnExercisesFirstButton: boolean
}

//parent property that will be used in this element
type ExercisesFormProps = ExercisesData & {
    updateFields: (fields: Partial<ExercisesData>) => void
}

export function ExerciseTable({
                                  newExercises,
                                  exercises,
                                  allMethods,
                                  showErrorModal,
                                  showOwnExercisesFirstButton,
                                  updateFields
                              }: ExercisesFormProps) {
    const [data, setData] = useState<Array<ExerciseTableData>>([]);
    const [showChooseAttachmentsModal, setShowChooseAttachmentsModal] = useState<boolean>(false);
    const [selectedExercise, setSelectedExercise] = useState<Exercise|null>(null);
    const [selectedExerciseAttachments, setSelectedExerciseAttachments] = useState<Array<Attachment>>([]);
    const [ownExercisesFirst, setOwnExercisesFirst] = useState<boolean>(true);

    function handleRowSelection(selectedRow: ExerciseTableData){
        let exercise = getExercise(selectedRow);
        if(exercise){
            setSelectedExercise(exercise);
            setSelectedExerciseAttachments(exercise.attachments||[]);
            setShowChooseAttachmentsModal(true);
        }
    }

    //definition of table columns
    const columns = useMemo<MRT_ColumnDef<ExerciseTableData>[]>(
            () => [
                {
                    accessorKey: 'name',
                    header: 'Übung/Tätigkeit',
                    filterFn: 'contains',
                },
                {
                    accessorKey: 'description',
                    header: 'Beschreibung',
                    filterFn: 'contains',
                    size: 300
                },
                {
                    accessorKey: 'method',
                    header: 'Methode',
                    filterFn: 'contains',
                    size: 50,
                    mantineTableHeadCellProps: {
                        align: 'center',
                    },
                    mantineTableBodyCellProps: {
                        align: 'center',
                    }

                },
                {
                    accessorKey: 'material',
                    header: 'Material',
                    filterFn: 'contains'
                },
                {
                    accessorKey: 'stages',
                    header: 'Stadien',
                    size: 50,
                    mantineTableHeadCellProps: {
                        align: 'center',
                    },
                    mantineTableBodyCellProps: {
                        align: 'center',
                    },
                }
            ],
            [newExercises]);

    /**
     * React to change of new exercises
     */
    useEffect(() => {
        if (ownExercisesFirst) {
            const username = Cookies.get("username");
            exercises = exercises.sort((a, b) => {
                const aCreatedBy = a.user?.username == username;
                const bCreatedBy = b.user?.username == username;
                if (aCreatedBy !== bCreatedBy) {
                    return aCreatedBy ? -1 : 1;
                }
                return a.name.localeCompare(b.name);
            })
        } else {
            exercises = exercises.sort((a, b) => a.name.localeCompare(b.name))
        }
        setData([...generateExerciseTableData(exercises)]);
    }, [exercises, ownExercisesFirst]);



    /**
     * Add exercise to list of new exercises
     * @param exerciseTableData exercise
     */
    function getExercise(exerciseTableData: ExerciseTableData) {
        //check if exercise not already in list
        if (newExercises.some(e => e.exercise.name === exerciseTableData.name
            && e.exercise.description === exerciseTableData.description && e.exercise.material === exerciseTableData.material)) {
            showErrorModal("Diese Tätigkeit befindet sich bereits in der Liste!");
        } else {
            const exerciseMethods = exerciseTableData.method.split(',')
            let dbMethods = []
            for (const method of exerciseMethods) {
                const dbMethod = allMethods.find(m => m.code === method.trim());
                if (dbMethod) {
                    dbMethods.push(dbMethod)
                }
            }

            if (dbMethods.length !== 0) {
                //check if exercise already in database
                let dbExercise = exercises.find(e => e.id === exerciseTableData.id);
                if (!dbExercise) {   //not in db
                    dbExercise = {
                        id: 0,
                        name: exerciseTableData.name,
                        description: exerciseTableData.description,
                        material: exerciseTableData.material,
                        methods: dbMethods,
                        stages: [],
                        topics: [],
                        attachments: [],
                        user: undefined,
                        parentId: undefined
                    }
                }
                return dbExercise;
            }
        }
        return null;
    }


    function setSelectedAttachmentsForExercise(attachments: Array<Attachment>){
        //update list with new exercises
        if(selectedExercise){
            let adaptNewExe = [...newExercises];
            let newPlanExercise: PlanExercise = {
                id: 0,
                exercise: selectedExercise,
                attachments: attachments,
                interest: undefined,
                execution: undefined,
                position: newExercises.length + 1
            }
            adaptNewExe.push(newPlanExercise);
            updateFields({newExercises: adaptNewExe})
        }
    }

    const table = useMantineReactTable<ExerciseTableData>({
        columns,
        data,
        initialState: {
            pagination: {
                pageSize: 5,
                pageIndex: 0
            }
        },
        autoResetPageIndex: false,
        enableColumnActions: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        positionPagination: "top",
        enableBottomToolbar: false,
        enableRowActions: true,
        positionActionsColumn: "last",
        globalFilterFn: 'contains',
        renderRowActions: ({row}) => {
            return <ActionIcon color="blueish" onClick={() => handleRowSelection(row.original)}>
                <IconSquareRoundedPlus/>
            </ActionIcon>
        },
        localization: MRT_Localization_DE,
        mantinePaginationProps: {
            showRowsPerPage: false
        },
        renderToolbarInternalActions: ({table}) => {
            return <Group>
                <MRT_ToggleGlobalFilterButton table={table}/>
                <MRT_ToggleFiltersButton table={table}/>
                {showOwnExercisesFirstButton && <Checkbox label="Eigene Übungen zuerst" checked={ownExercisesFirst}
                                                          onChange={(event) => setOwnExercisesFirst(event.currentTarget.checked)}/>}

            </Group>
        }
    });

    return (
        <>
            <Paper radius="xs" style={{marginTop: "1rem"}}>
                <MantineReactTable
                    table={table}
                />
            </Paper>
            <ChooseAttachmentsModal opened={showChooseAttachmentsModal} close={() => {
                setShowChooseAttachmentsModal(false);
            }} attachments={selectedExerciseAttachments} addAttachmentsToExercise={setSelectedAttachmentsForExercise} />
        </>
    );
}
